import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { authErrorInterceptor } from './interceptors/auth-error.interceptor';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideHashbrown } from '@hashbrownai/angular';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { SurveyModule } from 'survey-angular-ui';
import { provideAuth0, authHttpInterceptorFn, AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { AppInsightsService, FORMS_CONFIG, FORMS_BASE_PATH, tokenCacheInterceptor, workspaceHeaderInterceptor, WorkspaceService } from '@sentry/forms-core';
import { GlobalErrorHandler } from './services/global-error-handler';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideAppInitializer(() => { inject(AppInsightsService); }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    importProvidersFrom(SurveyModule, ToastrModule.forRoot({ positionClass: 'toast-bottom-right' })),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authHttpInterceptorFn, tokenCacheInterceptor, workspaceHeaderInterceptor, authErrorInterceptor])),
    {
      provide: FORMS_CONFIG,
      useFactory: () => ({
        apiUrl: environment.apiUrl,
        blobStorageUrl: environment.blobStorageUrl,
        appInsightsConnectionString: environment.appInsightsConnectionString,
      }),
    },
    { provide: FORMS_BASE_PATH, useValue: '' },
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.auth0.audience,
        scope:
          'openid profile email forms:read forms:write forms:delete submissions:read submissions:write workspaces_own:read user_info_own:read',
      },
      cacheLocation: 'localstorage',
      httpInterceptor: {
        allowedList: [
          {
            uri: `${environment.apiUrl}/api/*`,
            tokenOptions: {
              authorizationParams: { audience: environment.auth0.audience },
            },
          },
        ],
      },
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    provideHashbrown({
      baseUrl: `${environment.apiUrl}/api/chat`,
      middleware: [
        async (requestInit: RequestInit): Promise<RequestInit> => {
          const authService = inject(AuthService);
          const workspaceService = inject(WorkspaceService);

          const token = await firstValueFrom(
            authService.getAccessTokenSilently({
              authorizationParams: { audience: environment.auth0.audience },
            })
          );
          const workspaceId = workspaceService.activeWorkspace()?.id;

          return {
            ...requestInit,
            headers: {
              ...(requestInit.headers as Record<string, string>),
              Authorization: `Bearer ${token}`,
              ...(workspaceId ? { 'workspace': workspaceId } : {}),
            },
          };
        },
      ],
    }),
  ],
};
